import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import UnauthorizedAccess from '../../Pages/UnauthorizedAccess';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL || 'http://localhost:9000';
const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL || 'http://localhost:8000';

function AuditLogs() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedComments, setExpandedComments] = useState({});
  const [summary, setSummary] = useState({
    total_records: 0,
    total_adjustment: 0,
    total_charge: 0,
    total_discount: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    actionType: '',
    startDate: '',
    endDate: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [bookingIdSearch, setBookingIdSearch] = useState('');
  const [searchType, setSearchType] = useState('global');

  // Fetch current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
          credentials: 'include',
          headers: {
            accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }

        const userData = await response.json();
        if (userData && userData.id) {
          setCurrentUser(userData);
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      }
    };

    fetchCurrentUser();
  }, []);

  // Fetch audit logs
  useEffect(() => {
    if (currentUser && currentUser.roles && currentUser.roles.includes('Super_Admin')) {
      fetchAuditLogs();
    }
  }, [currentUser, filters, currentPage, itemsPerPage, searchType, bookingIdSearch]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      let url;

      if (searchType === 'booking' && bookingIdSearch) {
        url = `${BQ_BASE_URL}/bq/api/audit/booking/${bookingIdSearch}?skip=${skip}&take=${itemsPerPage}`;
      } else {
        url = `${BQ_BASE_URL}/bq/api/audit/logs?skip=${skip}&take=${itemsPerPage}`;
      }

      const response = await fetch(url, {
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch audit logs');
      }

      const data = await response.json();

      let filteredData = data.data || [];

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredData = filteredData.filter(
          (log) =>
            log.action?.toLowerCase().includes(searchLower) ||
            log.reason?.toLowerCase().includes(searchLower) ||
            log.comment?.toLowerCase().includes(searchLower) ||
            log.created_by_user?.full_name?.toLowerCase().includes(searchLower) ||
            log.created_by_user?.email?.toLowerCase().includes(searchLower) ||
            log.category?.toLowerCase().includes(searchLower)
        );
      }

      if (filters.category) {
        filteredData = filteredData.filter((log) => log.category === filters.category);
      }

      if (filters.actionType) {
        if (filters.actionType === 'discount') {
          filteredData = filteredData.filter((log) => log.is_discount === true);
        } else if (filters.actionType === 'charge') {
          filteredData = filteredData.filter((log) => log.is_discount === false);
        }
      }

      if (filters.startDate) {
        filteredData = filteredData.filter((log) => {
          const logDate = new Date(log.occurred_at);
          const startDate = new Date(filters.startDate);
          return logDate >= startDate;
        });
      }

      if (filters.endDate) {
        filteredData = filteredData.filter((log) => {
          const logDate = new Date(log.occurred_at);
          const endDate = new Date(filters.endDate);
          endDate.setHours(23, 59, 59, 999);
          return logDate <= endDate;
        });
      }

      setAuditLogs(filteredData);
      setSummary({
        total_records: data.total_records || filteredData.length,
        total_adjustment: data.total_adjustment || 0,
        total_charge: data.total_charge || 0,
        total_discount: data.total_discount || 0,
      });
      setTotalPages(data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return dateStr;
  };

  const toggleComment = (id) => {
    setExpandedComments((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Show unauthorized page if user is not Super_Admin
  if (currentUser && (!currentUser.roles || !currentUser.roles.includes('Super_Admin'))) {
    return <UnauthorizedAccess />;
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="p-6 min-h-screen bg-gray-100">
      {/* Header */}
      <header className="flex justify-between items-center mb-3 bg-white p-3 shadow-sm rounded-lg">
        <h1 className="text-black text-xl font-bold ml-2">Finance and Accounting</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/finance/billing-invoicing')}
            className="btn"
          >
            Billing and Invoicing
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/finance/accounts-receivable-payable')}
            className="btn"
          >
            Accounts Receivable/Payable
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/finance/financial-reporting')}
            className="btn"
          >
            Financial Reporting
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/finance/tax-management')}
            className="btn"
          >
            Tax Management
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/finance/audit-logs')}
            className="btn text-black font-bold"
          >
            Audit Logs
          </button>
        </div>
      </header>

      <div className="mt-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="text-gray-500 text-sm font-medium">Total Records</h3>
            <p className="text-2xl font-bold text-gray-900">{summary.total_records}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="text-gray-500 text-sm font-medium">Total Discounts</h3>
            <p className="text-2xl font-bold text-green-600">
              ₹{Math.abs(summary.total_discount).toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="text-gray-500 text-sm font-medium">Total Charges</h3>
            <p className="text-2xl font-bold text-red-600">
              ₹{Math.abs(summary.total_charge).toFixed(2)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h3 className="text-gray-500 text-sm font-medium">Net Impact</h3>
            <p
              className={`text-2xl font-bold ${summary.total_adjustment >= 0 ? 'text-red-600' : 'text-green-600'}`}
            >
              {summary.total_adjustment >= 0 ? '+' : ''}₹
              {Math.abs(summary.total_adjustment).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Search Type Toggle */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex space-x-4 mb-4">
            <button
              onClick={() => {
                setSearchType('global');
                setBookingIdSearch('');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                searchType === 'global'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Audit Logs
            </button>
            <button
              onClick={() => {
                setSearchType('booking');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                searchType === 'booking'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Booking Specific
            </button>
          </div>

          {searchType === 'booking' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Booking ID / Reservation Number
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Enter Booking ID (e.g., 7962138675)"
                  value={bookingIdSearch}
                  onChange={(e) => setBookingIdSearch(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
                />
                <button
                  onClick={() => fetchAuditLogs()}
                  className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Search
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                View all adjustments and charges for a specific reservation
              </p>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <input
              type="text"
              placeholder="Search by user, action, reason, category..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
            />

            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">All Categories</option>
              <option value="Room Rent">Room Rent</option>
              <option value="Food Order">Food & Beverages</option>
              <option value="Amenity Service">Amenities</option>
              <option value="Room Service">Room Service</option>
            </select>

            <select
              value={filters.actionType}
              onChange={(e) => setFilters({ ...filters, actionType: e.target.value })}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">All Transactions</option>
              <option value="discount">Discounts Only</option>
              <option value="charge">Charges Only</option>
            </select>

            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
              placeholder="From Date"
            />

            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900"
              placeholder="To Date"
            />
          </div>
        </div>

        {/* Audit Logs Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tax
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Comment / Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.length > 0 ? (
                    auditLogs.map((log, index) => (
                      <tr
                        key={log.id}
                        className={`border-b border-gray-100 ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } hover:bg-gray-50 transition-colors`}
                      >
                        <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
                          {formatDate(log.occurred_at)}
                        </td>
                        <td className="py-3 px-4">
                          <div className="text-sm font-medium text-gray-900">
                            {log.created_by_user?.full_name || 'System'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {log.created_by_user?.email || ''}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              log.is_discount
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {log.is_discount ? 'Discount' : 'Charge'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                            {log.category || '-'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`text-sm font-medium ${log.is_discount ? 'text-green-600' : 'text-red-600'}`}
                          >
                            {log.is_discount ? '-' : '+'}₹{Math.abs(log.amount).toFixed(2)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          ₹{log.tax_amount?.toFixed(2) || '0.00'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 max-w-xs">
                          <div className="truncate" title={log.reason || '-'}>
                            {log.reason || '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {log.comment ? (
                            <div className="text-sm">
                              <div className="text-gray-600">
                                {expandedComments[log.id]
                                  ? log.comment
                                  : truncateText(log.comment, 40)}
                              </div>
                              {log.comment.length > 40 && (
                                <button
                                  onClick={() => toggleComment(log.id)}
                                  className="text-xs text-blue-600 hover:text-blue-800 mt-1 focus:outline-none"
                                >
                                  {expandedComments[log.id] ? 'Show less' : 'Read more'}
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-12 text-gray-500">
                        <div className="flex flex-col items-center">
                          <svg
                            className="w-12 h-12 text-gray-400 mb-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <p className="text-lg font-medium">No audit logs found</p>
                          <p className="text-sm mt-1">Try adjusting your search or filters</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 0 && auditLogs.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Showing {auditLogs.length} of {summary.total_records} entries
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuditLogs;
